import { Alert, Badge, Card, PageHeader } from '@/components/ui';
import { useCalendarToday } from '@/lib/home-today-store';

/**
 * A página do Google Agenda.
 *
 * ⚠️ Ela quase não tem controle, e isso é honesto: **a configuração do Google
 * vive no servidor** (conta de serviço), não numa tela. O que esta página faz é
 * dizer, em português, **o que está acontecendo e quem resolve** — que é o que
 * faltava quando isso era um cartão no fim de uma página de 592 linhas.
 */
export function PaginaGoogle() {
  const calendar = useCalendarToday();

  const estado = calendar.notConfigured
    ? {
        rotulo: 'Não configurada',
        variante: 'neutral' as const,
        texto:
          'A conta de serviço do Google ainda não foi configurada neste ambiente. Quem cuida do servidor precisa preencher as informações de acesso.',
        quemResolve: 'quem cuida do servidor',
      }
    : calendar.scopeInsufficient
      ? {
          rotulo: 'Falta autorizar',
          variante: 'warning' as const,
          texto:
            'O administrador do Google Workspace da empresa precisa autorizar a leitura da agenda no painel de administração do Google.',
          quemResolve: 'o administrador do Google Workspace',
        }
      : calendar.isError
        ? {
            rotulo: 'Erro de conexão',
            variante: 'danger' as const,
            texto:
              calendar.error instanceof Error
                ? `Não foi possível consultar a agenda. Detalhe: ${calendar.error.message}`
                : 'Não foi possível consultar a agenda do Google.',
            quemResolve: 'quem cuida do servidor',
          }
        : calendar.isLoading || calendar.isFetching
          ? {
              rotulo: 'Verificando…',
              variante: 'neutral' as const,
              texto: 'Consultando a agenda do Google…',
              quemResolve: null,
            }
          : {
              rotulo: 'Conectada',
              variante: 'success' as const,
              texto: 'A agenda do Google está respondendo normalmente.',
              quemResolve: null,
            };

  return (
    <section className="min-w-0 space-y-6" data-testid="pagina-google">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Google Agenda"
        subtitle="Leitura da agenda para o Comercial. O acesso é configurado no servidor, não aqui — esta página diz como ele está."
      />

      <Card className="p-4 sm:p-5 space-y-3" data-testid="integracao-google">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">Estado</h2>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3">
              O que o sistema consegue ver da agenda agora.
            </p>
          </div>
          <Badge variant={estado.variante}>{estado.rotulo}</Badge>
        </div>

        <p className="text-sm text-ink-2 dark:text-ink-dark-2">{estado.texto}</p>

        {estado.quemResolve && (
          <Alert variant="info" rail>
            Isto não se resolve por esta tela: quem resolve é <strong>{estado.quemResolve}</strong>.
          </Alert>
        )}
      </Card>
    </section>
  );
}
